import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';
import { toast } from 'react-toastify';
import axios from 'axios';

const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [resendTimer, setResendTimer] = useState(60); // 60 seconds
  const [resendCooldown, setResendCooldown] = useState(false);

  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user && token) {
      toast.info('You are already logged in.');
      navigate('/');
    }
  }, []); // ✅ Only run once

  useEffect(() => {
    let timer;
    if (resendCooldown) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResendCooldown(false);
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
  };

  const isValidIraqiPhone = (number) => {
    const regex = /^7[4-9][0-9]{8}$/;
    return regex.test(number);
  };

  const sendVerificationCode = async () => {
    if (!isValidIraqiPhone(phone)) {
      setVerificationMessage('Please enter a valid Iraqi phone number.');
      return;
    }
    setSendingCode(true);
    try {
      await axios.post('/api/send-code', {
        phone: `+964${phone}`,
      });
      setCodeSent(true);
      setVerificationMessage('Verification code sent via WhatsApp.');

      // Start cooldown
      setResendCooldown(true);
      setResendTimer(60);
    } catch (error) {
      setVerificationMessage('Failed to send verification code. Try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode.trim()) {
      setVerificationMessage('Enter the verification code.');
      return;
    }
    setVerifying(true);
    try {
      await axios.post('/api/verify-code', {
        phone: `+964${phone}`,
        code: verificationCode,
      });
      setPhoneVerified(true);
      setVerificationMessage('Phone verified successfully!');
    } catch (error) {
      setAttempts((prev) => prev + 1);
      setVerificationMessage('Invalid verification code.');

      if (attempts + 1 >= 3) {
        setVerificationMessage('Too many failed attempts. Please resend the code.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);

    // if (mode === 'register' && !phoneVerified) {
    //   setError('Please verify your phone number first.');
    //   setIsLoggingIn(false);
    //   return;
    // }

    const url = `/api/users/${mode}`;
    const body = mode === 'login'
      ? { phone: `+964${phone}`, password }
      : { name, phone: `+964${phone}`, password };

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');

      if (data.token) {
        localStorage.setItem('token', data.token);

        const userRes = await fetch('/api/users/me', {
          headers: { Authorization: data.token },
        });
        const userData = await userRes.json();

        setUser(userData);
toast.success(`Welcome, ${userData.name}!`);

switch (userData.role) {
  case 'admin':
    navigate('/factory');
    break;
  case 'factory':
    navigate('/factory');
    break;
  case 'customer-service':
    navigate('/customer-service-dashboard');
    break;
  default:
    navigate('/');
}

      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded shadow w-full max-w-md space-y-6">
        <h2 className="text-2xl font-bold text-center">
          {mode === 'login' ? 'Login' : 'Create an Account'}
        </h2>

        {mode === 'register' && (
          <input
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full border p-3 rounded"
          />
        )}

        {/* Phone Input */}
        <div className="flex items-center w-full rounded border overflow-hidden">
          <span className="px-4 py-3 bg-gray-100 text-sm whitespace-nowrap border-r border-gray-300">
            +964
          </span>
          <input
            type="tel"
            placeholder="750 123 4567"
            value={phone}
            onChange={(e) => {
              const num = e.target.value.replace(/\D/g, '');
              setPhone(num);
            }}
            required
            className="w-full p-3 text-sm focus:outline-none"
          />
        </div>


        {/* Password Input */}
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full border p-3 rounded"
        />

        {/* Phone Verification Section */}
        {mode === 'register' && (
          <>
            {!codeSent && isValidIraqiPhone(phone) && (
              <button
                type="button"
                onClick={sendVerificationCode}
                disabled={sendingCode}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm flex items-center justify-center"
              >
                {sendingCode ? (
                  <div className="flex items-center gap-2">
                    <span className="loader small"></span> Sending...
                  </div>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            )}

            {codeSent && !phoneVerified && (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Enter Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full border p-3 rounded"
                />
                <button
                  type="button"
                  onClick={verifyCode}
                  disabled={verifying || attempts >= 3}
                  className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 text-sm flex items-center justify-center"
                >
                  {verifying ? (
                    <div className="flex items-center gap-2">
                      <span className="loader small"></span> Verifying...
                    </div>
                  ) : (
                    'Verify Code'
                  )}
                </button>

                {/* Resend Button */}
                {resendCooldown ? (
                  <button
                    type="button"
                    disabled
                    className="w-full bg-gray-300 text-gray-600 py-2 rounded text-sm"
                  >
                    Resend in {resendTimer}s
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={sendVerificationCode}
                    className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 text-sm"
                  >
                    Resend Code
                  </button>
                )}
              </div>
            )}

            {verificationMessage && (
              <p className={`text-sm ${phoneVerified ? 'text-green-600' : 'text-red-600'} text-center`}>
                {verificationMessage}
              </p>
            )}
          </>
        )}


        {error && <p className="text-red-500 text-center text-sm">{error}</p>}

        <button
          type="submit"
          disabled={isLoggingIn}
          className="w-full bg-black text-white py-3 rounded hover:bg-gray-900 transition"
        >
          {mode === 'login' ? 'Login' : 'Register'}
        </button>

        <p className="text-sm text-center">
          {mode === 'login' ? "Don't have an account?" : "Already have an account?"}{' '}
          <button
            type="button"
            onClick={switchMode}
            className="text-blue-600 underline"
          >
            {mode === 'login' ? 'Register' : 'Login'}
          </button>
        </p>
      </form>
    </div>
  );
};

export default AuthPage;
